'use client'

import type { SlotProps } from '.'

import * as React from 'react'
import {
  type RefObject,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { BodySmall, BodySubtext, variantStyles } from '@/components/atomic/text'
import { cn } from '@/utils/shadcn/utils'

enum SlotSide {
  LeftSlot = 'left-slot',
  RightSlot = 'right-slot',
}

interface BaseInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  width?: 'full' | string
  placeholderSize?: 'small' | 'large'
  slotProps?: SlotProps
  error?: boolean
  wrapperClassName?: string
}

function generateSlot({
  props,
  ref,
  slotSide,
}: {
  props: SlotProps
  ref: RefObject<HTMLDivElement | null>
  slotSide: SlotSide
}) {
  /** there are two different cases where we want to render the right
   *  slot, when side either 'right' or 'both' */
  const isRightSlot =
    props.side === 'right' ||
    (props.side === 'both' && slotSide === SlotSide.RightSlot)

  /** we may have passed either one or two icons, but we'll always end up
   *  rendering just one depending on if we're in the right or left slot */
  const icon =
    props.side === 'both'
      ? // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
        slotSide === SlotSide.LeftSlot
        ? props.leftIcon
        : props.rightIcon
      : props.icon

  /** getting the label text, wether we pass one or two labels we're always
   *  rendering only one at a time */
  const labelText =
    props.side === 'both'
      ? // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
        slotSide === SlotSide.LeftSlot
        ? props.leftLabel
        : props.rightLabel
      : props.label

  const renderedLabel = labelText ? (
    // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
    props.size === 'small' ? (
      <BodySubtext color={'text-secondary'} className={props.className}>
        {labelText}
      </BodySubtext>
    ) : (
      <BodySmall color={'text-secondary'} className={props.className}>
        {labelText}
      </BodySmall>
    )
  ) : null

  return (
    <div
      ref={ref}
      className={cn(
        'absolute px-3 py-1.5 h-full flex items-center overflow-hidden',
        props.containerClassName,
        props.className,
        isRightSlot ? 'right-0 rounded-r-md' : 'rounded-l-md',
      )}
    >
      <div className="relative flex items-center justify-center whitespace-nowrap">
        {/* rendering slots on one side */}
        {icon || renderedLabel}
      </div>

      {props.showSeparator && (
        <div
          className={`absolute ${slotSide === SlotSide.LeftSlot ? 'right-0' : 'left-0'} top-0 bottom-0 m-auto w-[1px] bg-moment-blue-grey-200 dark:bg-moment-blue-grey-1000`}
          style={{ height: props.separatorHeight }}
        />
      )}
    </div>
  )
}

const Input = React.forwardRef<HTMLInputElement, BaseInputProps>(
  (
    {
      className,
      type,
      error,
      placeholderSize,
      wrapperClassName,
      slotProps,
      width,
      disabled,
      defaultValue,
      ...props
    },
    ref,
  ) => {
    const leftSlotRef = useRef<HTMLDivElement>(null)
    const rightSlotRef = useRef<HTMLDivElement>(null)
    const innerInputRef = useRef<HTMLInputElement>(null)
    useImperativeHandle(ref, () => innerInputRef.current as HTMLInputElement)

    // Use a ref to track the previous side value
    const prevSideRef = useRef<string | undefined>(slotProps?.side)

    const [leftPadding, setLeftPadding] = useState<number>(0)
    const [rightPadding, setRightPadding] = useState<number>(0)

    // biome-ignore lint/correctness/useExhaustiveDependencies: Bulk suppression as part of biome migration
    useLayoutEffect(() => {
      // Check if the side has changed
      const sideChanged = prevSideRef.current !== slotProps?.side

      // Force recalculation of padding when refs change or when the side changes
      if (
        leftSlotRef.current &&
        (leftSlotRef.current.offsetWidth !== leftPadding || sideChanged)
      ) {
        setLeftPadding(leftSlotRef.current.offsetWidth)
      }

      if (
        rightSlotRef.current &&
        (rightSlotRef.current.offsetWidth !== rightPadding || sideChanged)
      ) {
        setRightPadding(rightSlotRef.current.offsetWidth)
      }

      // Update the previous side ref after calculations
      if (sideChanged) {
        prevSideRef.current = slotProps?.side
      }
    }, [
      leftSlotRef.current?.offsetWidth,
      rightSlotRef.current?.offsetWidth,
      innerInputRef.current?.offsetWidth,
      leftPadding,
      rightPadding,
      slotProps?.side,
    ])

    const inputClassName = useMemo(() => {
      return cn(
        `flex px-3 py-1 gap-2.5 items-center self-stretch transition-colors
         text-foreground
         border border-solid rounded-md border-primary-border bg-background
         placeholder:text-moment-blue-grey-400 dark:placeholder:text-moment-grey-400
         focus-visible:outline-hidden focus-visible:ring-2 focus-visible:border-1 focus-visible:border-moment-teal-600 focus-visible:ring-moment-teal-500 dark:focus-visible:border-moment-teal-500 dark:focus-visible:ring-moment-teal-900
         disabled:cursor-not-allowed disabled:bg-moment-white-200 dark:disabled:bg-moment-black-200 dark:disabled:border-moment-blue-grey-1000 dark:disabled:text-moment-grey-700
         [&.error]:outline-hidden [&.error]:ring-2 [&.error]:border-1 [&.error]:border-moment-red-600 [&.error]:ring-moment-red-200 dark:[&.error]:border-moment-red-400 dark:[&.error]:ring-moment-red-900`,
        !disabled
          ? 'hover:border-moment-teal-400 dark:hover:border-moment-teal-800'
          : '',
        placeholderSize === 'small'
          ? variantStyles.bodySubtext
          : variantStyles.bodySmall,
        error ? 'error' : '',
        width === 'full' ? 'w-full' : width,
        className,
      )
    }, [error, width, className, placeholderSize, disabled])

    return (
      <div
        className={cn(
          'relative flex items-center',
          width === 'full' ? 'w-full' : width,
          wrapperClassName,
        )}
      >
        {slotProps &&
          (slotProps.side === 'left' || slotProps.side === 'both') &&
          generateSlot({
            props: slotProps,
            ref: leftSlotRef,
            slotSide: SlotSide.LeftSlot,
          })}

        <input
          type={type}
          className={inputClassName}
          // Using style to dynamically update the padding based on the width of the content in the slot
          // Note: Need to add 12px to the padding so that text input is not flush with the separator
          style={{
            paddingLeft:
              slotProps &&
              (slotProps.side === 'both' || slotProps.side === 'left')
                ? `${leftPadding + (slotProps.showSeparator ? 12 : 0)}px`
                : undefined,
            paddingRight:
              // biome-ignore lint/complexity/useOptionalChain: Bulk suppression as part of biome migration
              slotProps &&
              slotProps.showSeparator &&
              (slotProps.side === 'right' || slotProps.side === 'both')
                ? `${rightPadding + (slotProps.showSeparator ? 12 : 0)}px`
                : undefined,
          }}
          ref={innerInputRef}
          data-lpignore="true"
          disabled={disabled}
          defaultValue={defaultValue}
          {...props}
        />

        {slotProps &&
          (slotProps.side === 'right' || slotProps.side === 'both') &&
          generateSlot({
            props: slotProps,
            ref: rightSlotRef,
            slotSide: SlotSide.RightSlot,
          })}
      </div>
    )
  },
)
Input.displayName = 'Input'

export { Input }
